
import { useState, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { prepareProductData } from './prepareProductData';
import { createProduct } from './createProduct';
import { updateExistingProduct } from './updateProduct';
import type { UseProductSaveProps, SaveResult } from './types';
import type { Product } from '@/lib/supabase/products/types';

export const useProductSave = ({
  productId,
  isNewProduct,
  productState,
  onSuccess,
  shopId,
  organizationId
}: UseProductSaveProps) => {
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const defaultFormId = productState.formId || null;
  
  console.log("useProductSave initialized with:", { 
    productId, 
    isNewProduct, 
    shopId, 
    organizationId,
    defaultFormId,
    "mediaUrls": productState.mediaUrls?.length || 0
  });

  const handleSave = useCallback(async (updatedMediaUrls?: string[]) => {
    if (saving) return;
    
    setSaving(true);
    console.log("Beginning product save operation");
    
    try {
      // Use provided media URLs if available, otherwise use state
      const mediaUrlsToSave = updatedMediaUrls || productState.mediaUrls;
      console.log("Media URLs to save:", mediaUrlsToSave);
      
      // Merge updated media URLs with product state and prepare data
      const productDataToSave = prepareProductData({
        ...productState,
        mediaUrls: mediaUrlsToSave,
        shopId,
        organizationId
      }, isNewProduct);
      
      console.log("Prepared product data:", {
        name: productDataToSave.name,
        mediaUrls: productDataToSave.media_urls,
        shopId: productDataToSave.shop_id,
        organizationId: productDataToSave.organization_id
      });
      
      let saveResult: SaveResult;
      
      if (isNewProduct) {
        console.log("Creating new product with organization ID:", organizationId);
        saveResult = await createProduct({
          productData: productDataToSave,
          shopId,
          organizationId: organizationId || ''
        });
      } else {
        console.log("Updating existing product ID:", productId);
        // Correction: Pass all required parameters to updateExistingProduct
        saveResult = await updateExistingProduct(
          productId,
          productDataToSave,
          productState.initialQuantity || 0,
          productState.quantity || 0
        );
      }
      
      if (saveResult.success) {
        console.log("Product saved successfully:", saveResult.productId);
        toast({
          title: isNewProduct ? "Produit créé" : "Produit mis à jour",
          description: isNewProduct ? "Le produit a été créé avec succès" : "Le produit a été mis à jour avec succès"
        });
        
        if (onSuccess) {
          onSuccess(saveResult.productId || productId);
        }
      } else {
        throw saveResult.error || new Error("Une erreur est survenue lors de l'enregistrement du produit");
      }
    } catch (error) {
      console.error("Error during product save:", error);
      toast({
        title: "Erreur",
        description: error instanceof Error ? error.message : "Une erreur est survenue lors de l'enregistrement du produit",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  }, [productState, isNewProduct, productId, toast, saving, shopId, organizationId, onSuccess]);

  return { saving, handleSave, defaultFormId };
};

export { DEFAULT_BENEFITS, DEFAULT_FAQS } from './defaults';
export type { UseProductSaveProps } from './types';
